// src/pages/Favorites.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import StorageService from '../utils/storage';
import LazyImage from '../components/LazyImage';
import MainLayout from '../components/MainLayout';

const Content = styled.div`
  padding: 40px;
`;

const ChannelsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  padding: 0 10px;
  
  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ChannelCard = styled(Link)`
  position: relative;
  background: rgba(26, 16, 51, 0.5);
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.3s ease;
  text-decoration: none;
  display: flex;
  align-items: center;
  padding: 15px;
  gap: 20px;
  
  &:hover {
    transform: translateY(-2px);
    background: rgba(43, 27, 84, 0.8);
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.3);
  }
`;

const ChannelLogo = styled.div`
  width: 80px;
  height: 80px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 8px;
  overflow: hidden;
  
  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }
`;

const ChannelInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const ChannelName = styled.div`
  color: white;
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 8px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ChannelGroup = styled.div`
  color: rgba(255, 255, 255, 0.7);
  font-size: 14px;
  margin-bottom: 5px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const QualityBadge = styled.div`
  position: absolute;
  top: 15px;
  right: 15px;
  background-color: ${props => {
    switch (props.$quality) {
      case 'SD': return '#666';
      case 'HD': return '#4caf50';
      case 'FHD': return '#2196f3';
      case 'FHD H265': return '#1976d2';
      case 'FHD HDR': return '#0d47a1';
      case '4K': return '#E50914';
      case '4K H265': return '#b71c1c';
      case '4K HDR': return '#7f0000';
      default: return '#666';
    }
  }};
  color: white;
  font-size: 12px;
  font-weight: bold;
  padding: 4px 8px;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
`;

const RemoveButton = styled.button`
  position: absolute;
  right: 15px;
  bottom: 15px;
  background: rgba(229, 9, 20, 0.9);
  color: white;
  border: none;
  border-radius: 4px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 4px;
  
  &:hover {
    background: #F40D17;
    transform: scale(1.05);
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  background: rgba(26, 16, 51, 0.3);
  border-radius: 8px;
  margin-top: 20px;
  
  h2 {
    font-size: 24px;
    font-weight: 600;
    margin-bottom: 16px;
    color: white;
  }
  
  p {
    color: rgba(255, 255, 255, 0.7);
    font-size: 16px;
    line-height: 1.6;
    max-width: 600px;
    margin: 0 auto;
  }
`;

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: 600;
  margin: 0 0 30px;
  color: white;
`;

const Favorites = () => {
  const [favorites, setFavorites] = useState([]);

  useEffect(() => {
    const loadFavorites = () => {
      const savedFavorites = StorageService.getFavorites();
      setFavorites(savedFavorites);
    };

    loadFavorites();
  }, []);

  const handleRemoveFavorite = (e, channelId) => {
    e.preventDefault();
    e.stopPropagation();

    const updated = StorageService.removeFavorite(channelId);
    setFavorites(updated);
  };

  const detectQuality = (name) => {
    if (!name) return 'SD';

    const nameLower = name.toLowerCase();

    if (nameLower.includes('4k') && nameLower.includes('hdr')) return '4K HDR';
    if (nameLower.includes('4k') && nameLower.includes('h265')) return '4K H265';
    if (nameLower.includes('4k')) return '4K';
    if (nameLower.includes('fhd') && nameLower.includes('hdr')) return 'FHD HDR';
    if (nameLower.includes('fhd') && nameLower.includes('h265')) return 'FHD H265';
    if (nameLower.includes('fhd') || nameLower.includes('1080')) return 'FHD';
    if (nameLower.includes('hd') || nameLower.includes('720')) return 'HD';

    return 'SD';
  };

  return (
    <MainLayout>
      <Content>
        <PageTitle>Favoritos</PageTitle>

        {favorites.length === 0 ? (
          <EmptyState>
            <h2>Nenhum favorito ainda</h2>
            <p>
              Ao assistir um canal, clique no ícone de estrela para adicioná-lo aos seus favoritos.
              Seus canais favoritos aparecerão aqui para acesso rápido.
            </p>
          </EmptyState>
        ) : (
          <ChannelsGrid>
            {favorites.map(channel => (
              <ChannelCard to={`/watch/${encodeURIComponent(channel.id)}`} key={channel.id}>
                <ChannelLogo>
                  <LazyImage
                    src={channel.logo || '/placeholder.png'}
                    alt={channel.name}
                    onError={(e) => {
                      if (e.target.src !== '/placeholder.png') {
                        e.target.src = '/placeholder.png';
                      }
                    }}
                  />
                </ChannelLogo>
                <ChannelInfo>
                  <ChannelName>{channel.name}</ChannelName>
                  <ChannelGroup>{channel.group}</ChannelGroup>
                </ChannelInfo>
                <QualityBadge $quality={detectQuality(channel.name)}>
                  {detectQuality(channel.name)}
                </QualityBadge>
                <RemoveButton onClick={(e) => handleRemoveFavorite(e, channel.id)}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" fill="currentColor" />
                  </svg>
                  Remover
                </RemoveButton>
              </ChannelCard>
            ))}
          </ChannelsGrid>
        )}
      </Content>
    </MainLayout>
  );
};

export default Favorites;